import { type FC, useRef } from 'react';
import { selectTriggerSize } from '@components/classes/select';
import Icon from '@components/Icon';
import { InputField } from '@components/InputField';
import InputReadOnly from '@components/InputReadOnly';
import { InputSelectMenuContainer, inputSelectStyles } from '@components/InputSelect';
import { SelectMenu } from '@components/SelectMenu/SelectMenu';
import type { SelectMenuItem } from '@components/SelectMenu/TreeView';
import type { ElementSize } from '@components/types';
import { useSchemaStore } from '@ContractBuilder/store';
import type { BlockVisibilityValueType } from '@domain/schemas/block.schema';
import sharedClasses from '@utils/shared-classes';
import { getSelectMenuItemsNoClauses } from '@WysiwygEditor/components/Controls/utils/datapoint-options';
import clsx from 'clsx';
import type { ControllerStateAndHelpers, DownshiftState, StateChangeOptions } from 'downshift';
import Downshift from 'downshift';

import type { VisibilityConfigPath } from '../types';

export interface SelectMenuItemResult extends Omit<SelectMenuItem, 'type'> {
  title?: string;
  type?: string;
}

interface BlockVisibilityFieldSelectorProps {
  idx: number;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  label?: string;
  name: string;
  onChange: (value: BlockVisibilityValueType | undefined, name: VisibilityConfigPath) => void;
  placeholder?: string;
  size?: ElementSize;
  value?: SelectMenuItemResult | null;
}

const downshiftStateReducer = (
  _state: DownshiftState<SelectMenuItemResult>,
  changes: StateChangeOptions<SelectMenuItemResult>,
) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return { ...changes, inputValue: '' };

    case Downshift.stateChangeTypes.clickButton:
    case Downshift.stateChangeTypes.changeInput:
      return { ...changes, highlightedIndex: 0 };

    default:
      return changes;
  }
};

export const BlockVisibilityFieldSelector: FC<BlockVisibilityFieldSelectorProps> = ({
  name,
  idx,
  isReadOnly,
  isDisabled,
  label,
  placeholder = 'Select a field',
  onChange,
  size = 'md',
  value,
}) => {
  const anchorRef = useRef<HTMLElement>(null);
  const { schema } = useSchemaStore(({ schema }) => ({ schema }));
  const SELECTABLE_SCHEMA_ITEMS = getSelectMenuItemsNoClauses(schema);
  const onSelect = (
    selectedItem: SelectMenuItemResult | null,
    _stateAndHelpers: ControllerStateAndHelpers<SelectMenuItemResult>,
  ) => {
    return onChange(selectedItem?.id, name as VisibilityConfigPath);
  };

  return (
    <InputField aria-label={name} labelText={label} name={name}>
      {isReadOnly && <InputReadOnly name={name} value={value?.title || ''} />}
      {!isReadOnly && (
        <Downshift<SelectMenuItemResult>
          stateReducer={downshiftStateReducer}
          selectedItem={value}
          itemToString={(item) => item?.title ?? ''}
          onSelect={onSelect}
        >
          {({ getItemProps, getMenuProps, getToggleButtonProps, isOpen, getInputProps }) => {
            return (
              <div className="relative flex">
                <button
                  disabled={isDisabled}
                  data-testid="datapoint-condition"
                  {...getToggleButtonProps()}
                  type="button"
                  ref={anchorRef as React.RefObject<HTMLButtonElement>}
                  className={clsx(
                    'border-info-300 text-info-900 m-auto flex flex-1 justify-between rounded-md border px-3 py-2.5 text-sm shadow-xs',
                    selectTriggerSize({ size }),
                    isDisabled && sharedClasses.disabled,
                  )}
                  aria-label={`Select field #${idx + 1}`}
                >
                  {value?.label ? value.label : <span className="text-info-400">{placeholder}</span>}
                  {!isOpen && <Icon name="chevron-down" className="text-info-800 ml-2 self-center fill-current" />}
                  {isOpen && <Icon name="chevron-up" className="text-primary-700 ml-2 self-center fill-current" />}
                </button>

                {isOpen && (
                  <InputSelectMenuContainer
                    anchorRef={anchorRef}
                    className={clsx(inputSelectStyles.menuOuter, 'w-max overflow-y-auto')}
                    shouldRenderInsidePortal
                  >
                    <div data-testid="downshift-list">
                      <SelectMenu
                        isSearchable
                        items={SELECTABLE_SCHEMA_ITEMS}
                        getAdditionalMenuProps={() => getMenuProps({}, { suppressRefError: true })}
                        getAdditionalItemProps={(item: SelectMenuItem) => getItemProps({ item })}
                        getAdditionalInputProps={getInputProps}
                      />
                    </div>
                  </InputSelectMenuContainer>
                )}
              </div>
            );
          }}
        </Downshift>
      )}
    </InputField>
  );
};
